import type { FileMetadata } from '../../../../types'
import type { FileUploadStatus } from './hooks'

export type MergedPhotoItem = {
  key: string
  type: 'existing' | 'uploading'
  fileId?: string
  label: string
  file?: FileMetadata
  uploadFile?: FileUploadStatus
}

/**
 * Merge the album's stored files and the in-flight upload list into one
 * ordered list, so a tile transitions smoothly from upload preview to loaded
 * image instead of the preview disappearing and a second tile appearing.
 */
export function mergePhotoItems(
  albumFiles: FileMetadata[],
  uploadFiles: FileUploadStatus[],
): MergedPhotoItem[] {
  const result: MergedPhotoItem[] = []
  const matchedUploadNames = new Set<string>()

  // Add existing files, checking if they have a matching completed upload
  // (for the transition).
  for (const file of albumFiles) {
    // Check if this file has a matching completed upload (by label/name)
    const matchingUpload = uploadFiles.find(
      u => u.name === file.label && u.status === 'done'
    )

    if (matchingUpload) {
      matchedUploadNames.add(matchingUpload.name)
    }

    result.push({
      key: file.id,
      type: 'existing',
      fileId: file.id,
      label: file.label,
      file,
      uploadFile: matchingUpload, // Include for smooth transition
    })
  }

  // Add uploading files that don't yet have a matching albumFile
  for (const upload of uploadFiles) {
    if (!matchedUploadNames.has(upload.name)) {
      result.push({
        key: `uploading-${upload.name}`,
        type: 'uploading',
        label: upload.name,
        uploadFile: upload,
      })
    }
  }

  return result
}
